export type ContactInfoInput = {
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  stateCode: string;
  zipCode: string;
  country: string;
};

export type ContactErrors = Partial<Record<keyof ContactInfoInput, string>>;

export const ADDRESS_WITH_HASH = /^[A-Za-z0-9.,#\-'\s]+$/;       // Street 1/2
export const CITY_PATTERN = /^[A-Za-z0-9.,\-'\s]+$/;             // City/state text (no #)
export const MAX_LEN = 255;
export const MAX_POSTAL = 10;
export const ZIP_US = /^(?!00000)\d{5}(?:-\d{4})?$/;             // allow ZIP+4
export const ZIP_CA = /^[a-z]\d[a-z] \d[a-z]\d$/i;      // exact A1A 1A1 (space required)
export const OTHER_POSTAL = /^[a-z0-9.,\-'\s]+$/i;              // generic for non-US/CA

const normalizeCountry = (c?: string) => (c || '').trim().toLowerCase();
export const isUS = (c?: string) =>
  ['us', 'usa', 'united states', 'united states of america'].includes(normalizeCountry(c));
export const isCA = (c?: string) =>
  ['ca', 'can', 'canada'].includes(normalizeCountry(c));

export const validateContactInfo = (ci: ContactInfoInput): ContactErrors => {

  const errors: ContactErrors = {};

  const street1 = ci.streetAddress1?.trim() || '';
  const street2 = ci.streetAddress2?.trim() || '';
  const city = ci.city?.trim() || '';
  const stateCode = ci.stateCode?.trim() || '';
  const zip = ci.zipCode?.trim() || '';
  const country = ci.country?.trim() || '';

  const us = isUS(country);
  const ca = isCA(country);

  // Country/Region (required)
  if (!country) {
    errors.country = 'Country/Region is required';
  }

  // Street Address 1
  if (!street1) {
    errors.streetAddress1 = 'Does not meet the requirements. Street Address 1 is required';
  } else if (street1.length > MAX_LEN) {
    errors.streetAddress1 = 'Does not meet the requirements. Street Address 1 can be at most 255 characters long.';
  } else if (!ADDRESS_WITH_HASH.test(street1)) {
    errors.streetAddress1 =
      "Does not meet the requirements. Street Address 1 may contain only letters, numbers, and the following special symbols: dash (-), period (.), comma (,), hashtag(#), apostrophe ('), and space";
  }

  // Street Address 2 (optional)
  if (street2) {
    if (street2.length > MAX_LEN) {
      errors.streetAddress2 = 'Does not meet the requirements. Street Address 2 can be at most 255 characters long.';
    } else if (!ADDRESS_WITH_HASH.test(street2)) {
      errors.streetAddress2 =
        "Does not meet the requirements. Street Address 2 may contain only letters, numbers, and the following special symbols: dash (-), period (.), comma (,), hashtag(#), apostrophe ('), and space";
    }
  }

  // City
  if (!city) {
    errors.city = 'City is required';
  } else if (city.length > MAX_LEN) {
    errors.city = 'Does not meet the requirements. City must be no more than 255 characters long.';
  } else if (!CITY_PATTERN.test(city)) {
    errors.city =
      "Does not meet the requirements. No special characters allowed other than the following: dash (-), period (.), comma (,), apostrophe ('), and space";
  }

  // State, Province, or Region
  if (us || ca) {
    if (!stateCode) {
      errors.state = 'State is required';
    }
  } else if (stateCode && !CITY_PATTERN.test(stateCode)) {
    errors.state =
      "Does not meet the requirements. No special characters allowed other than the following: dash (-), period (.), comma (,), apostrophe ('), and space";
  }

  // Zip or Postal Code
  if (zip && zip.length > MAX_POSTAL) {
    errors.zipCode = 'Does not meet the requirements. Postal Code can be at most 10 characters long.';
  } else if (us || ca) {
    if (!zip) {
      errors.zipCode = 'Postal Code is required';
    } else if (us && !ZIP_US.test(zip)) {
      errors.zipCode = 'Does not meet the requirements. US Zip Code must be between 00001 and 99999.';
    } else if (ca && !ZIP_CA.test(zip)) {
      errors.zipCode =
        "Does not meet the requirements. Canada Postal Code must be in A1A 1A1 format, where 'A' is any letter and '1' is any digit, with a space separating the 3rd and 4th characters.";
    }
  } else if (zip && !OTHER_POSTAL.test(zip)) {
    errors.zipCode =
      "Does not meet the requirements. No special characters allowed other than the following: dash (-), period (.), comma (,), apostrophe ('), and space";
  }

  return errors;
};